// Management → Employees: the employee database. Structured people records (identity, job, contact).
// Active employees automatically appear on the Overtime roster. Same access as the rest of
// Management: production:read to view, production:write to edit.

import { useMemo, useState } from 'react';
import { EMPLOYEE_TEAMS, type Employee } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useDeleteEmployee, useEmployees } from '../features/employees.js';
import { EmployeeFormModal } from '../components/EmployeeFormModal.js';
import { IconTrash } from '../components/icons.js';
import { useFormatDate } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import {
  EmptyState, ErrorState, FilterBar, FilterSelect, FilterToggle, InlineError, LoadingState, PageHeader, Pill, SearchInput, useConfirm,
} from '../components/ui.js';

// a phone number as a dialable tel: link (spaces and dashes dropped from the target, kept in the label)
const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

export function Employees() {
  const { can } = useAuth();
  const canWrite = can('production:write');
  const { data, isLoading, isFetching, error, refetch } = useEmployees();
  const del = useDeleteEmployee();
  const [confirmNode, confirm] = useConfirm();
  const fmtDate = useFormatDate();
  const isPhone = useIsPhone();

  const [q, setQ] = useState('');
  const [teamF, setTeamF] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [editId, setEditId] = useState<number | null | undefined>(undefined); // undefined=closed, null=new

  const all = data?.employees ?? [];
  const departments = useMemo(() => [...new Set(all.map((e) => e.department).filter(Boolean))].sort(), [all]);
  const [deptF, setDeptF] = useState('');

  const rows = useMemo(() => {
    const query = fold(q.trim());
    return all.filter((e) => {
      if (activeOnly && !e.active) return false;
      if (teamF && !e.teams.includes(teamF)) return false;
      if (deptF && e.department !== deptF) return false;
      if (query) {
        const hay = [e.name, e.employeeNo, ...e.teams, e.department, e.jobTitle, e.phone, e.email].join(' ');
        if (!hasText(query, hay)) return false;
      }
      return true;
    });
  }, [all, q, teamF, deptF, activeOnly]);

  const editingInitial = useMemo<(Employee & { id: number }) | undefined>(
    () => (typeof editId === 'number' ? all.find((e) => e.id === editId) : undefined),
    [editId, all],
  );

  const activeCount = all.filter((e) => e.active).length;
  // "active only" is the default, so turning it off is what counts as a filter
  const activeFilters = (q.trim() ? 1 : 0) + (teamF ? 1 : 0) + (deptF ? 1 : 0) + (activeOnly ? 0 : 1);
  const clearFilters = () => { setQ(''); setTeamF(''); setDeptF(''); setActiveOnly(true); };

  const remove = async (e: Employee) => {
    if (await confirm({ title: 'Delete employee', message: `Delete “${e.name}”? This also removes them from the Overtime roster.`, confirmLabel: 'Delete', danger: true })) del.mutate(e.id);
  };

  return (
    <>
      <PageHeader title="Employees">
        <span className="muted page-count">{rows.length} shown · {activeCount} active</span>
        {canWrite && <button className="btn btn-sm btn-primary" onClick={() => setEditId(null)}>+ Employee</button>}
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Refreshing…' : 'Refresh'}</button>
      </PageHeader>
      <FilterBar search={<SearchInput value={q} onChange={setQ} placeholder={isPhone ? 'Search people' : 'Search name / no. / job / phone / email'} width={280} />} activeCount={activeFilters} onClear={clearFilters}>
        <FilterSelect
          label="Team"
          ariaLabel="Filter by team"
          value={teamF}
          onChange={setTeamF}
          options={[{ value: '', label: 'All teams' }, ...EMPLOYEE_TEAMS.map((t) => ({ value: t, label: t }))]}
        />
        <FilterSelect
          label="Department"
          ariaLabel="Filter by department"
          value={deptF}
          onChange={setDeptF}
          options={[{ value: '', label: 'All departments' }, ...departments.map((d) => ({ value: d, label: d }))]}
        />
        <FilterToggle checked={activeOnly} onChange={setActiveOnly}>Active only</FilterToggle>
      </FilterBar>
      {del.isError && <InlineError error={del.error} onDismiss={() => del.reset()} />}

      {isLoading && <LoadingState label="Loading employees…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && rows.length === 0 && (
        <EmptyState title="No employees" hint={all.length ? 'No matches for these filters.' : 'Add people with “+ Employee”. Active employees appear on the Overtime roster.'} />
      )}

      {data && rows.length > 0 && (
        <div className="tbl-wrap">
          {/* phones: a card per person: name + status, "no. · teams · department", "job · start", the phone
              and email as tap-to-call / tap-to-mail chips, then Edit and Delete. Writers tap the card to edit. */}
          <table className="tbl tbl-cards">
            <thead>
              <tr>
                <th>Name</th><th>Emp. no.</th><th>Teams</th><th>Department</th><th>Job title</th>
                <th>Start date</th><th>Phone</th><th>Email</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const idLine = [e.employeeNo, e.teams.join(', '), e.department].filter(Boolean).join(' · ');
                const jobLine = [e.jobTitle, fmtDate(e.startDate)].filter(Boolean).join(' · ');
                return (
                  <tr key={e.id} style={{ opacity: e.active ? 1 : 0.6 }}
                    className={isPhone && canWrite ? 'tc-tap' : undefined}
                    onClick={isPhone && canWrite ? () => setEditId(e.id) : undefined}>
                    <td className="tc-title" style={{ fontWeight: 600, cursor: canWrite ? 'pointer' : 'default' }} onClick={() => canWrite && setEditId(e.id)}>{e.name}</td>
                    <td className="tc-hide">{e.employeeNo || '-'}</td>
                    <td className="tc-hide">{e.teams.length ? e.teams.join(', ') : '-'}</td>
                    <td className="tc-hide">{e.department || '-'}</td>
                    <td className="tc-hide">{e.jobTitle || '-'}</td>
                    <td className="muted tc-hide">{fmtDate(e.startDate) || '-'}</td>
                    <td className={`tc-meta emp-meta${idLine ? '' : ' tc-hide'}`}>{idLine}</td>
                    <td className={`tc-meta emp-meta${jobLine ? '' : ' tc-hide'}`}>{jobLine}</td>
                    <td className="tc-hide">{e.phone ? <a href={telHref(e.phone)}>{e.phone}</a> : '-'}</td>
                    <td className="tc-hide">{e.email ? <a href={`mailto:${e.email}`}>{e.email}</a> : '-'}</td>
                    <td className={`tc-full emp-contact${e.phone || e.email ? '' : ' tc-hide'}`} onClick={(ev) => ev.stopPropagation()}>
                      {e.phone && <a className="emp-chip" href={telHref(e.phone)} aria-label={`Call ${e.name}: ${e.phone}`}>{e.phone}</a>}
                      {e.email && <a className="emp-chip" href={`mailto:${e.email}`} aria-label={`Email ${e.name}: ${e.email}`}>{e.email}</a>}
                    </td>
                    <td className="tc-end">{e.active ? <Pill kind="info">active</Pill> : <Pill kind="warn">left</Pill>}</td>
                    <td className={`${canWrite ? 'tc-actions' : 'tc-hide'} emp-acts`} style={{ whiteSpace: 'nowrap' }} onClick={(ev) => ev.stopPropagation()}>
                      {canWrite && (
                        <>
                          <button className="btn btn-sm" onClick={() => setEditId(e.id)}>Edit</button>
                          {/* Delete is a neutral ghost on every list, card and row alike (danger tint on hover / focus); the confirm carries the red */}
                          <button className="btn btn-sm btn-ghost btn-del" disabled={del.isPending} onClick={() => void remove(e)}><IconTrash size={14} /> Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editId !== undefined && (
        <EmployeeFormModal
          initial={editingInitial}
          onClose={() => setEditId(undefined)}
          onSaved={() => void refetch()}
        />
      )}
      {confirmNode}
    </>
  );
}
